import React, { useEffect } from "react";
import { Button, Form, Input, Modal, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import type { RapFormValues } from "../types";
import type { Market } from "types/market";
import type { Rep } from "types/rep";

export type RapFormModalProps = {
  open: boolean;
  mode: "add" | "edit";
  markets: Market[];
  marketsLoading?: boolean;
  loading?: boolean;
  initialRap?: Rep | null;
  onClose: () => void;
  onSubmit: (values: RapFormValues) => void;
};

const RapFormModal: React.FC<RapFormModalProps> = ({
  open,
  mode,
  markets,
  marketsLoading = false,
  loading = false,
  initialRap,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm<RapFormValues>();
  const hasFallbackMarket =
    mode === "edit" &&
    Boolean(initialRap?.market_id) &&
    !markets.some((market) => market.id === initialRap?.market_id);
  const marketOptions = [
    ...markets.map((market) => ({
      value: market.id,
      label: market.name,
    })),
    ...(hasFallbackMarket && initialRap?.market_id
      ? [
          {
            value: initialRap.market_id,
            label: initialRap.market_name || "Assigned market",
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (mode === "edit" && initialRap) {
      form.setFieldsValue({
        name: initialRap.name,
        email: initialRap.email,
        phone: initialRap.phone,
        password: "",
        market_id: initialRap.market_id,
      });
      return;
    }

    form.resetFields();
  }, [open, mode, initialRap, form]);

  const handleCancel = () => {
    onClose();
    form.resetFields();
  };

  const handleFinish = (values: RapFormValues) => {
    onSubmit(values);
  };

  return (
    <Modal
      title={mode === "edit" ? "Edit Rap" : "Add Rap"}
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      width={520}
      destroyOnClose
      className="add-mart-modal rap-modal modal-round"
      closeIcon={<CloseOutlined className="add-category-close-icon" />}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="name"
          rules={[{ required: true, message: "Please enter rap name" }]}
        >
          <Input placeholder="Rap Name" size="large" className="input-round" />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input placeholder="Email" size="large" className="input-round" />
        </Form.Item>

        <Form.Item
          name="phone"
          rules={[
            { required: true, message: "Please enter phone" },
            {
              pattern: /^\+[1-9]\d{6,14}$/,
              message: "Use international format, e.g. +2348000000001",
            },
          ]}
        >
          <Input placeholder="Phone" size="large" className="input-round" />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[
            ...(mode === "add"
              ? [{ required: true, message: "Please enter password" }]
              : []),
          ]}
        >
          <Input.Password
            placeholder={mode === "edit" ? "Password (leave blank to keep)" : "Password"}
            size="large"
            className="input-round"
          />
        </Form.Item>

        <Form.Item
          name="market_id"
          rules={[{ required: true, message: "Please select a market" }]}
        >
          <Select
            size="large"
            className="input-round"
            placeholder="Assigned Market"
            loading={marketsLoading}
            disabled={marketsLoading || marketOptions.length === 0}
            notFoundContent={
              marketsLoading ? "Loading markets..." : "No markets available."
            }
            options={marketOptions}
          />
        </Form.Item>

        <Form.Item className="mb-0 add-mart-modal-footer">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            className="markets-add-btn btn-round"
            loading={loading}
            disabled={loading || marketsLoading || marketOptions.length === 0}
          >
            {mode === "edit" ? "Update" : "Add Rap"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RapFormModal;
